import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert
} from '@mui/material';
import { invoiceAPI, vendorAPI, categoryAPI, usageAPI } from '../api/api';

const EditApiUsageDialog = ({ open, onClose, usage, onSave, onError }) => {
  const [formData, setFormData] = useState({
    application_name: '',
    token_uses: '',
    usage_unit: '',
    usage_description: '',
    cost: '',
    usage_date: '',
    status: '',
    vendor_id: '',
    category_id: '',
    invoice_id: ''
  });
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [error, setError] = useState('');
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Load vendors, categories and invoices when dialog opens
  useEffect(() => {
    if (open) {
      loadOptions();
      if (usage && usage.invoice_id) {
        loadPdf();
      }
    }
  }, [open, usage]);

  // Load vendors, categories and invoices
  const loadOptions = async () => {
    setLoadingOptions(true);
    setError('');
    
    try {
      const [vendorsResponse, categoriesResponse, invoicesResponse] = await Promise.all([
        vendorAPI.getVendors(),
        categoryAPI.getCategories(),
        invoiceAPI.getInvoices()
      ]);
      
      setVendors(vendorsResponse.data);
      setCategories(categoriesResponse.data);
      setInvoices(invoicesResponse.data);
    } catch (err) {
      console.error('Error loading options:', err);
      let errorMessage = 'Failed to load vendors, categories, and invoices';
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      }
      setError(errorMessage);
    } finally {
      setLoadingOptions(false);
    }
  };

  // Load PDF for the associated invoice
  const loadPdf = async () => {
    if (!usage || !usage.invoice_id) return;
    
    setPdfLoading(true);
    try {
      const response = await invoiceAPI.getInvoicePdf(usage.invoice_id);
      if (response && response.data) {
        const contentType = response.headers['content-type'];
        if (contentType && contentType.includes('application/pdf')) {
          const blob = new Blob([response.data], { type: 'application/pdf' });
          const url = URL.createObjectURL(blob);
          setPdfUrl(url);
        }
      }
    } catch (err) {
      console.error('Error loading PDF:', err);
      // We can continue even if PDF fails to load
    } finally {
      setPdfLoading(false);
    }
  };

  // Initialize form data when usage changes
  useEffect(() => {
    if (usage) {
      setFormData({
        application_name: usage.application_name || '',
        token_uses: usage.token_uses || '',
        usage_unit: usage.usage_unit || '',
        usage_description: usage.usage_description || '',
        cost: usage.cost || '',
        usage_date: usage.usage_date || '',
        status: usage.status || '',
        vendor_id: usage.vendor_id || '',
        category_id: usage.category_id || '',
        invoice_id: usage.invoice_id || ''
      });
    }
  }, [usage]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    if (!usage) return;
    
    setLoading(true);
    setError('');
    
    try {
      await usageAPI.updateApiUsage(usage.id, formData);
      onSave();
      onClose();
    } catch (err) {
      console.error('Error updating API usage:', err);
      let errorMessage = 'Failed to update API usage';
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      }
      setError(errorMessage);
      onError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError('');
    onClose();
  };

  if (!usage) return null;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <DialogTitle>
        Edit API Usage
      </DialogTitle>
      
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flex: 1,
          p: 0,
          overflow: 'hidden'
        }}
      >
        {error && (
          <Alert severity="error" sx={{ m: 2, width: '100%' }}>
            {error}
          </Alert>
        )}
        
        {/* PDF Viewer Section - Left Side */}
        <Box
          sx={{
            width: '50%',
            borderRight: '1px solid #e0e0e0',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            backgroundColor: '#ffffff'
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              overflow: 'auto',
              p: 2
            }}
          >
            {pdfLoading ? (
              <CircularProgress />
            ) : pdfUrl ? (
              <iframe 
                src={pdfUrl} 
                title="PDF Viewer" 
                width="100%" 
                height="100%" 
                style={{ border: 'none' }} 
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                No PDF available
              </Typography>
            )}
          </Box>
        </Box>

        {/* Editable Data Section - Right Side */}
        <Box
          sx={{
            width: '50%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            backgroundColor: '#f5f5f5',
            p: 3
          }}
        >
          {loadingOptions ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
              <CircularProgress />
            </Box>
          ) : (
            <Box component="form" sx={{ pt: 1 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>API Usage Details</Typography>
              
              <TextField
                margin="dense"
                name="application_name"
                label="Application Name"
                fullWidth
                variant="outlined"
                value={formData.application_name}
                onChange={handleChange}
                sx={{ mb: 2 }}
              />
              
              <TextField
                margin="dense"
                name="token_uses"
                label="Quantity"
                type="number"
                fullWidth
                variant="outlined"
                value={formData.token_uses}
                onChange={handleChange}
                sx={{ mb: 2 }}
              />
              
              <TextField
                margin="dense"
                name="usage_unit"
                label="Unit"
                fullWidth
                variant="outlined"
                value={formData.usage_unit}
                onChange={handleChange}
                sx={{ mb: 2 }}
              />
              
              <TextField
                margin="dense"
                name="usage_description"
                label="Description"
                fullWidth
                variant="outlined"
                value={formData.usage_description}
                onChange={handleChange}
                sx={{ mb: 2 }}
              />
              
              <TextField
                margin="dense"
                name="cost"
                label="Cost"
                type="number"
                fullWidth
                variant="outlined"
                value={formData.cost}
                onChange={handleChange}
                sx={{ mb: 2 }}
              />
              
              <TextField
                margin="dense"
                name="usage_date"
                label="Usage Date"
                type="date"
                fullWidth
                variant="outlined"
                value={formData.usage_date}
                onChange={handleChange}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{ mb: 2 }}
              />
              
              <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  label="Status"
                  onChange={handleChange}
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                </Select>
              </FormControl>
              
              <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
                <InputLabel>Vendor</InputLabel>
                <Select
                  name="vendor_id"
                  value={formData.vendor_id}
                  label="Vendor"
                  onChange={handleChange}
                >
                  {vendors.map(vendor => (
                    <MenuItem key={vendor.id} value={vendor.id}>
                      {vendor.standard_name || vendor.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  name="category_id"
                  value={formData.category_id}
                  label="Category"
                  onChange={handleChange}
                >
                  {categories.map(category => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.standard_name || category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
                <InputLabel>Invoice</InputLabel>
                <Select
                  name="invoice_id"
                  value={formData.invoice_id}
                  label="Invoice"
                  onChange={handleChange}
                >
                  {invoices.map(invoice => (
                    <MenuItem key={invoice.id} value={invoice.id}>
                      {invoice.invoice_number || `Invoice ${invoice.id}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading || loadingOptions}
        >
          {loading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Saving...
            </>
          ) : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditApiUsageDialog;